import React from 'react';

const DEFAULT_GUTTER_WIDTH = 26;

export interface RowProps {
  children?: React.ReactNode;
  className?: string;
  align?: 'normal' | 'start' | 'center' | 'end' | 'stretch';
  component?: React.ElementType;
  debug?: boolean;
  gutterWidth?: number;
  justify?:
    | 'start'
    | 'center'
    | 'end'
    | 'between'
    | 'around'
    | 'initial'
    | 'inherit';
  nogutter?: boolean;
  onClick?: React.MouseEventHandler;
  style?: React.CSSProperties;
  tabIndex?: number;
}

const ALIGN_MAP: Record<string, string> = {
  normal: 'normal',
  start: 'flex-start',
  center: 'center',
  end: 'flex-end',
  stretch: 'stretch'
};

const JUSTIFY_MAP: Record<string, string> = {
  start: 'flex-start',
  center: 'center',
  end: 'flex-end',
  between: 'space-between',
  around: 'space-around',
  initial: 'initial',
  inherit: 'inherit'
};

const Row: React.FC<RowProps> = ({
  children,
  className,
  align,
  component: Component = 'div',
  debug,
  gutterWidth,
  justify,
  nogutter,
  onClick,
  style,
  tabIndex
}) => {
  const gutter = nogutter ? 0 : (gutterWidth ?? DEFAULT_GUTTER_WIDTH);
  const halfGutter = gutter / 2;

  const rowStyle: React.CSSProperties & { [key: `--${string}`]: string } = {
    display: 'flex',
    flexWrap: 'wrap',
    boxSizing: 'border-box',
    marginLeft: `-${halfGutter}px`,
    marginRight: `-${halfGutter}px`,
    ['--gutter-width']: `${gutter}px`,
    ['--gutter-half-width']: `${halfGutter}px`,
    ...style
  };

  if (align) {
    rowStyle.alignItems = ALIGN_MAP[align] || align;
  }

  if (justify) {
    rowStyle.justifyContent = JUSTIFY_MAP[justify] || justify;
  }

  if (debug) {
    rowStyle.border = '1px solid blue';
  }

  return (
    <Component
      className={className}
      style={rowStyle}
      onClick={onClick}
      tabIndex={tabIndex}
    >
      {children}
    </Component>
  );
};

export default Row;
